/**
 * Dialog "EDIT SALE PLAN" — mirror Access form
 * `frmMatchingPSMainSubFormEditPlan_s` (file
 * `frontend/plan/access_all/form_frmMatchingPSMainSubFormEditPlan_s.txt`).
 *
 * Layout chuẩn Access: top = read-only contract context (Buyer, Sales Contract,
 * Contract Date, Shipment From, ContractQuant, Quality, Grade, đã-instructed-3-loại);
 * giữa = section "Delete Old plan & Insert new plan" với 3 hàng checkbox (BL / NT /
 * Direct P-S), mỗi hàng có cột "Tick Plan" (checkbox), "Input Quantity" (TextField
 * số), "Choose Purchase" (combobox — chỉ dòng Direct). Match Date + Note đặt
 * bên phải. Save Plan button ở góc phải trên section "Insert".
 *
 *   ┌──── Read-only context (3 col grid) ────┐
 *   │ Sales Contract │ Contract Date │ Buyer  │
 *   │ ContractQuant │ Quality       │ Grade  │
 *   │ Shipment From │ Inst-BL/NT/PS (3 chip) │
 *   └────────────────────────────────────────┘
 *   ┌──── Delete Old plan & Insert new plan ─┐
 *   │ [☐] Instruction - Bảo Lộc   [    kg]   │
 *   │ [☐] Instruction - Nhơn Trạch[    kg]   │
 *   │ [☐] Direct P-S              [    kg] [Purchase ▼] │
 *   │ Match Date: [date]   Note: [text]      │
 *   └────────────────────────────────────────┘
 *
 * VBA logic dịch sang React (xem cuối file form spec):
 * - `divide_number()`     → chia đều quantity_kg cho số checkbox được tick,
 *                            dư mod gắn vào checkbox đầu tiên (theo thứ tự VBA:
 *                            Direct > NT > BL → mod gắn vào Direct).
 * - `btnSavePlan_Click()` → 6 validation tuần tự + soft-delete + insert nhiều rows.
 *   Validation server-side cùng logic (matching_ps_service.save_edit_plan_s),
 *   client check trước để báo lỗi sớm.
 */
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs, { type Dayjs } from 'dayjs'
import { useEffect, useMemo, useState } from 'react'

import { useSnackbar } from '../../../hooks/useSnackbar'
import {
  useDeleteDoFromPlanMutation,
  useGetActiveDirectPurchaseQuery,
  useGetDosByContractQuery,
  useGetEditPlanSPurchaseCandidatesQuery,
  useLazyGetDosByMatchQuery,
  useSaveEditPlanSMutation,
  type DoByMatch,
  type EditPlanPurchaseCandidate,
} from '../../../store/api/matchingPsApi'
import ConfirmDialog from '../../../components/ConfirmDialog'
import CreateDoFromPlanDialog, {
  type DoDraftSeed,
  type EditDoRow,
} from './CreateDoFromPlanDialog'

const ACCENT = '#fb8c00' // S column accent (orange)

export interface EditSalePlanDialogProps {
  open: boolean
  onClose: () => void
  /** Row đang được chọn ở table Sale (đã camelCase từ `_row_to_item`). */
  row: Record<string, unknown> | null
  /** Permission gating (Layer 4) — false → ẩn Save + show read-only banner. */
  canEdit?: boolean
}

function toNum(v: unknown): number {
  if (v == null || v === '') return 0
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}

function formatThousand(n: number): string {
  if (!Number.isFinite(n)) return ''
  return Math.round(n).toLocaleString('en-US')
}

function formatAccessDate(v: unknown): string {
  if (!v) return ''
  const d = dayjs(String(v))
  return d.isValid() ? d.format('DD-MMM-YYYY') : String(v)
}

/** ins_side_cb display: `contract_no - <pending #,###> - quality_code - category`. */
function formatPurchaseOption(c: EditPlanPurchaseCandidate): string {
  const parts = [
    c.contract_no,
    formatThousand(toNum(c.p_plan_pending)),
    c.quality_code ?? '',
    c.category ?? '',
  ]
  return parts.filter((p) => p !== '').join(' - ')
}

/**
 * Wrapper outer — chỉ chịu trách nhiệm mount/unmount inner component theo `open`.
 * Inner component giữ toàn bộ state; remount khi `row.contractNo` đổi đảm bảo
 * state reset mà không cần `setState` trong useEffect (tránh cascading renders).
 */
export default function EditSalePlanDialog({ open, onClose, row, canEdit = true }: EditSalePlanDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      // `keepMounted={false}` (default) → khi `open=false` MUI unmount children
      // → state reset tự động cho lần mở kế tiếp.
    >
      {open && row ? (
        <EditSalePlanDialogContent
          key={String(row.contractNo ?? '')}
          row={row}
          onClose={onClose}
          canEdit={canEdit}
        />
      ) : null}
    </Dialog>
  )
}

interface EditSalePlanDialogContentProps {
  row: Record<string, unknown>
  onClose: () => void
  canEdit: boolean
}

function EditSalePlanDialogContent({ row, onClose, canEdit }: EditSalePlanDialogContentProps) {
  const snack = useSnackbar()

  // ── Read-only fields lấy thẳng từ selected row ─────────────────────────
  const contractNo = String(row.contractNo ?? '')
  const cpName = String(row.cpName ?? '')
  const contractDate = formatAccessDate(row.contractDate)
  const deliveryFrom = formatAccessDate(row.deliveryFrom)
  const quantityKg = toNum(row.quantityKg)
  const qualityCode = String(row.qualityCode ?? '')
  const grade = String(row.category ?? '')
  const instructedBL = toNum(row.sInstructBaoloc)
  const instructedNT = toNum(row.sInstructNhontrach)
  const instructedDirect = toNum(row.sDirectP)

  // ── Edit state ─────────────────────────────────────────────────────────
  // Load sẵn Tick Plan + Số từ plan đã có (instructed values).
  const [chbBL, setChbBL] = useState(() => instructedBL > 0)
  const [chbNT, setChbNT] = useState(() => instructedNT > 0)
  const [chbDirect, setChbDirect] = useState(() => instructedDirect > 0)
  const [insBL, setInsBL] = useState<string>(() => (instructedBL > 0 ? String(instructedBL) : '0'))
  const [insNT, setInsNT] = useState<string>(() => (instructedNT > 0 ? String(instructedNT) : '0'))
  const [insDirect, setInsDirect] = useState<string>(
    () => (instructedDirect > 0 ? String(instructedDirect) : '0'),
  )
  const [insSide, setInsSide] = useState<string>('')
  const [matchDate, setMatchDate] = useState<Dayjs | null>(() => dayjs())
  const [note, setNote] = useState<string>('')

  // Purchase — fetch khi dialog open (inner mount = open).
  const candidatesQuery = useGetEditPlanSPurchaseCandidatesQuery()
  const candidates = useMemo(() => candidatesQuery.data ?? [], [candidatesQuery.data])

  const selectedCandidate = useMemo(
    () => candidates.find((c) => c.contract_no === insSide) ?? null,
    [candidates, insSide],
  )
  const insSidePending = toNum(selectedCandidate?.p_plan_pending)

  // ── divide_number — VBA: chia đều quantity_kg / số tick, mod (kg dư) dồn ──
  // vào checkbox đầu tiên theo thứ tự Direct > NT > BL.
  const runDivide = (nextBL: boolean, nextNT: boolean, nextDirect: boolean) => {
    const count = (nextBL ? 1 : 0) + (nextNT ? 1 : 0) + (nextDirect ? 1 : 0)
    if (count === 0) {
      setInsBL('0')
      setInsNT('0')
      setInsDirect('0')
      // VBA: khi bỏ tick Direct → cũng clear ins_side_cb.
      if (!nextDirect) setInsSide('')
      return
    }
    const divide = Math.floor(quantityKg / count)
    const mod = quantityKg - divide * count
    let modCheck = true
    let nextDirectKg = 0
    let nextNTKg = 0
    let nextBLKg = 0
    // Match thứ tự VBA: Direct → NT → BL (mod dồn vào cái đầu tiên được tick).
    if (nextDirect && divide > 0) {
      nextDirectKg = divide
      if (mod !== 0 && modCheck) {
        nextDirectKg += mod
        modCheck = false
      }
    } else if (!nextDirect) {
      setInsSide('')
    }
    if (nextNT && divide > 0) {
      nextNTKg = divide
      if (mod !== 0 && modCheck) {
        nextNTKg += mod
        modCheck = false
      }
    }
    if (nextBL && divide > 0) {
      nextBLKg = divide
      if (mod !== 0 && modCheck) {
        nextBLKg += mod
        modCheck = false
      }
    }
    setInsDirect(String(nextDirectKg))
    setInsNT(String(nextNTKg))
    setInsBL(String(nextBLKg))
  }

  const toggleChbBL = (next: boolean) => {
    setChbBL(next)
    runDivide(next, chbNT, chbDirect)
  }
  const toggleChbNT = (next: boolean) => {
    setChbNT(next)
    runDivide(chbBL, next, chbDirect)
  }
  const toggleChbDirect = (next: boolean) => {
    setChbDirect(next)
    runDivide(chbBL, chbNT, next)
    // Tick Direct mà contract đã có Direct với 1 Purchase trước đó → autoload Select Purchase.
    if (next && !insSide.trim() && activeDirect?.pSide) {
      setInsSide(activeDirect.pSide)
    }
  }

  // ── Save mutation ──────────────────────────────────────────────────────
  const [save, { isLoading: saving }] = useSaveEditPlanSMutation()

  // Create DO sau khi Save có phần match mới (direct / baoloc / nhontrach).
  const [doOpen, setDoOpen] = useState(false)
  const [doSeeds, setDoSeeds] = useState<DoDraftSeed[]>([])
  const [doEditRows, setDoEditRows] = useState<EditDoRow[]>([])
  const [doMode, setDoMode] = useState<'create' | 'edit'>('create')
  const [doScenario, setDoScenario] = useState<'direct' | 'instruct'>('direct')
  // Confirm "xoá DO + update plan" khi 1 loại bị đưa về 0 mà còn DO đã book.
  const [confirmDelSaveOpen, setConfirmDelSaveOpen] = useState(false)
  const [orphanedDos, setOrphanedDos] = useState<DoByMatch[]>([])
  const [deleteDoRow, { isLoading: deletingDos }] = useDeleteDoFromPlanMutation()
  const [fetchDosByMatch] = useLazyGetDosByMatchQuery()

  // DO đã book cho contract này (side S) → text alert + orphan detect.
  const { data: existingDos } = useGetDosByContractQuery(
    { contractNo, side: 'S' },
    { skip: !contractNo },
  )

  // Direct match active sẵn có → autoload combo Select Purchase khi tick Direct P-S.
  const { data: activeDirect } = useGetActiveDirectPurchaseQuery(
    { contractNo },
    { skip: !contractNo },
  )
  // Direct đã tick sẵn (plan cũ) → load luôn Select Purchase từ direct match active.
  useEffect(() => {
    if (chbDirect && !insSide.trim() && activeDirect?.pSide) {
      setInsSide(activeDirect.pSide)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDirect])

  // Thực thi save plan. `deleteOrphanDos` = true: xoá các DO mồ côi trước khi save.
  const performSaveAndDo = async (deleteOrphanDos: boolean) => {
    const baolocKg = toNum(insBL)
    const nhontrachKg = toNum(insNT)
    const directKg = toNum(insDirect)
    try {
      if (deleteOrphanDos) {
        for (const d of orphanedDos) {
          await deleteDoRow({ doId: d.deliveryOrderScsId }).unwrap()
        }
      }
      const res = await save({
        contract_no: contractNo,
        match_date: matchDate ? matchDate.format('YYYY-MM-DD') : '',
        note: note.trim() || undefined,
        ins_instruct_baoloc: chbBL ? baolocKg : 0,
        ins_instruct_nhontrach: chbNT ? nhontrachKg : 0,
        ins_direct: chbDirect ? directKg : 0,
        ins_side: chbDirect ? insSide.trim() : undefined,
        quantity_kg: quantityKg,
      }).unwrap()
      snack.success('Successfully Updated')

      const createdMatches = res.created_matches ?? []
      // Mở Create DO cho TẤT CẢ component vừa tạo (direct + BL + NT có qty>0).
      if (createdMatches.length === 0) {
        onClose()
        return
      }
      const seeds: DoDraftSeed[] = createdMatches.map((cm) =>
        cm.kind === 'direct'
          ? {
              saleNo: contractNo,
              buyer: cpName,
              toWh: '',
              doQty: cm.match_kg,
              planRef: cm.p_side ?? '',
              planRefSub: '',
              planKg: cm.match_kg,
              matchType: 'direct',
              matchId: cm.match_id,
              fromWhEditable: true,
            }
          : {
              saleNo: contractNo,
              buyer: cpName,
              toWh: cm.from_wh,
              doQty: cm.match_kg,
              planRef: cm.from_wh,
              planRefSub: 'TLC warehouse',
              planKg: cm.match_kg,
              matchType: 'instruct',
              matchId: cm.match_id,
              fromWhEditable: false,
            },
      )
      // Load DO theo MATCH_ID (BE đã re-point DO sang match mới khi save): mỗi
      // created match → fetch DO của đúng match đó → editRows gắn planId tương ứng.
      // Plan chưa có DO → dialog tự tạo 1 dòng default.
      const edits: EditDoRow[] = []
      for (let i = 0; i < createdMatches.length; i++) {
        const cm = createdMatches[i]
        const dos = await fetchDosByMatch({ matchId: cm.match_id, matchType: cm.match_type })
          .unwrap()
          .catch(() => [])
        for (const d of dos) {
          edits.push({
            doId: d.deliveryOrderScsId,
            doNo: d.doNo ?? '',
            toWh: d.fromWarehouse ?? '',
            doQty: Number(d.doQty) || 0,
            note: d.note ?? '',
            planId: i,
          })
        }
      }
      setDoSeeds(seeds)
      setDoEditRows(edits)
      setDoMode(edits.length > 0 ? 'edit' : 'create')
      setDoScenario(seeds.some((s) => s.matchType === 'direct') ? 'direct' : 'instruct')
      setDoOpen(true)
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'data' in e
          ? String((e as { data?: { detail?: string } }).data?.detail ?? 'Save failed.')
          : e instanceof Error
            ? e.message
            : 'Save failed.'
      snack.error(msg)
    }
  }

  const handleSave = () => {
    if (!canEdit) {
      snack.error("You don't have permission for this action.")
      return
    }
    const directKg = toNum(insDirect)
    const baolocKg = toNum(insBL)
    const nhontrachKg = toNum(insNT)

    // Mirror VBA validation chain.
    if (!chbBL && !chbNT && !chbDirect) {
      snack.warning('Please select at least one type to plan!')
      return
    }
    if (directKg + baolocKg + nhontrachKg > quantityKg) {
      snack.warning('Recheck: Total Input Quantity <= Quantity Kg!')
      return
    }
    if (chbDirect && !insSide.trim()) {
      snack.warning('Please choose Purchase (for Direct P-S)!')
      return
    }
    if (chbDirect && directKg > insSidePending && insSidePending > 0) {
      snack.warning('Recheck: Direct P-S <= Purchase Pending Quantity!')
      return
    }
    if (directKg < 0 || baolocKg < 0 || nhontrachKg < 0) {
      snack.warning('Recheck: quantities must be >= 0!')
      return
    }
    if (!matchDate || !matchDate.isValid()) {
      snack.warning('Please choose Match Date!')
      return
    }

    // Qty hiệu lực mỗi loại → loại nào về 0 mà còn DO đã book = orphan.
    const dirEff = chbDirect ? directKg : 0
    const blEff = chbBL ? baolocKg : 0
    const ntEff = chbNT ? nhontrachKg : 0
    const orphaned = (existingDos ?? []).filter(
      (d) =>
        (d.kind === 'direct' && dirEff === 0) ||
        (d.kind === 'baoloc' && blEff === 0) ||
        (d.kind === 'nhontrach' && ntEff === 0),
    )
    if (orphaned.length > 0) {
      setOrphanedDos(orphaned)
      setConfirmDelSaveOpen(true)
      return
    }
    void performSaveAndDo(false)
  }

  const readOnlyField = {
    size: 'small' as const,
    InputProps: { readOnly: true },
    InputLabelProps: { shrink: true },
    fullWidth: true,
  }

  return (
    <>
      <DialogTitle
        title="EDIT SALE PLAN"
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: { xs: 0.75, sm: 1.25 },
          bgcolor: alpha(ACCENT, 0.1),
          borderBottom: `2px solid ${alpha(ACCENT, 0.55)}`,
          fontSize: { xs: '0.85rem', sm: '1rem' },
          fontWeight: 700,
          letterSpacing: 0.3,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        EDIT SALE PLAN
      </DialogTitle>
      <DialogContent dividers sx={{ p: { xs: 1.25, sm: 2 } }}>
        {!canEdit ? (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Read-only — you don't have edit permission for this module.
          </Alert>
        ) : null}
        {/* ── Read-only context block ─────────────────────────────────── */}
        <Grid container spacing={1.25}>
          <Grid item xs={12} sm={4}>
            <TextField {...readOnlyField} label="Sales Contract" value={contractNo} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField {...readOnlyField} label="Contract Date" value={contractDate} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField {...readOnlyField} label="Buyer" value={cpName} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              {...readOnlyField}
              label="ContractQuant"
              value={formatThousand(quantityKg)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField {...readOnlyField} label="Quality" value={qualityCode} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField {...readOnlyField} label="Grade" value={grade} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField {...readOnlyField} label="Shipment From" value={deliveryFrom} />
          </Grid>
          <Grid item xs={12} sm={8}>
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              sx={{ pt: 0.5, alignItems: 'center' }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 110 }}>
                Already instructed:
              </Typography>
              <Chip
                size="small"
                label={`Bảo Lộc · ${formatThousand(instructedBL)}`}
                sx={{ bgcolor: alpha(ACCENT, 0.1), color: ACCENT, fontWeight: 600 }}
              />
              <Chip
                size="small"
                label={`Nhơn Trạch · ${formatThousand(instructedNT)}`}
                sx={{ bgcolor: alpha(ACCENT, 0.1), color: ACCENT, fontWeight: 600 }}
              />
              <Chip
                size="small"
                label={`Direct (to P) · ${formatThousand(instructedDirect)}`}
                sx={{ bgcolor: alpha(ACCENT, 0.1), color: ACCENT, fontWeight: 600 }}
              />
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.4 }}
          >
            Delete Old plan &amp; Insert new plan
          </Typography>
        </Divider>

        {/* ── Editable 3-row plan ──────────────────────────────────────
            Layout: 3 cột 4/3/5 trong Paper "table" có border + divider giữa
            header và từng row → 3 cột nối thẳng cross-row. */}
        <Box
          sx={{
            border: (t) => `1px solid ${alpha(t.palette.text.primary, 0.12)}`,
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          {/* Header row */}
          <Grid
            container
            sx={{
              display: { xs: 'none', md: 'flex' },
              bgcolor: (t) => alpha(t.palette.text.primary, 0.04),
              borderBottom: (t) => `1px solid ${alpha(t.palette.text.primary, 0.1)}`,
            }}
          >
            <Grid item md={4} sx={{ px: 1.25, py: 0.75 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                Tick Plan
              </Typography>
            </Grid>
            <Grid
              item
              md={3}
              sx={{
                px: 1.25,
                py: 0.75,
                borderLeft: { xs: 0, md: '1px solid rgba(0,0,0,0.08)' },
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                Input Quantity (kg)
              </Typography>
            </Grid>
            <Grid
              item
              md={5}
              sx={{
                px: 1.25,
                py: 0.75,
                borderLeft: { xs: 0, md: '1px solid rgba(0,0,0,0.08)' },
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                Choose Purchase (for Direct P-S)
              </Typography>
            </Grid>
          </Grid>

          {/* Row: Bảo Lộc */}
          <Grid
            container
            alignItems="center"
            sx={{ borderBottom: (t) => `1px solid ${alpha(t.palette.text.primary, 0.08)}` }}
          >
            <Grid item xs={12} md={4} sx={{ px: 1.25, py: 0.75 }}>
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={chbBL}
                    onChange={(e) => toggleChbBL(e.target.checked)}
                  />
                }
                label={<Typography variant="body2">Instruction - Bảo Lộc</Typography>}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
              sx={{
                px: 1.25,
                py: 0.75,
                borderLeft: { xs: 0, md: '1px solid rgba(0,0,0,0.08)' },
              }}
            >
              <TextField
                size="small"
                fullWidth
                type="text"
                value={formatThousand(toNum(insBL))}
                onChange={(e) => setInsBL(e.target.value.replace(/[^\d]/g, ''))}
                disabled={!chbBL}
                inputProps={{ inputMode: 'numeric', style: { textAlign: 'right' } }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={5}
              sx={{
                px: 1.25,
                py: 0.75,
                borderLeft: { xs: 0, md: '1px solid rgba(0,0,0,0.08)' },
                color: 'text.disabled',
              }}
            >
              <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                — not applicable
              </Typography>
            </Grid>
          </Grid>
          {chbBL && (String(row.sInstructBaolocLst ?? '') || instructedBL > toNum(row.sInstructBaolocDo)) ? (
            <Box sx={{ px: 1.25, pb: 0.75, bgcolor: (t) => alpha(t.palette.warning.main, 0.08) }}>
              {String(row.sInstructBaolocLst ?? '') ? (
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                  Booked DO: <b>{String(row.sInstructBaolocLst)}</b>
                </Typography>
              ) : null}
              {instructedBL > toNum(row.sInstructBaolocDo) ? (
                <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 600 }}>
                  ⚠ Planned but DO not fully booked — add DO after saving.
                </Typography>
              ) : null}
            </Box>
          ) : null}

          {/* Row: Nhơn Trạch */}
          <Grid
            container
            alignItems="center"
            sx={{ borderBottom: (t) => `1px solid ${alpha(t.palette.text.primary, 0.08)}` }}
          >
            <Grid item xs={12} md={4} sx={{ px: 1.25, py: 0.75 }}>
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={chbNT}
                    onChange={(e) => toggleChbNT(e.target.checked)}
                  />
                }
                label={<Typography variant="body2">Instruction - Nhơn Trạch</Typography>}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
              sx={{
                px: 1.25,
                py: 0.75,
                borderLeft: { xs: 0, md: '1px solid rgba(0,0,0,0.08)' },
              }}
            >
              <TextField
                size="small"
                fullWidth
                type="text"
                value={formatThousand(toNum(insNT))}
                onChange={(e) => setInsNT(e.target.value.replace(/[^\d]/g, ''))}
                disabled={!chbNT}
                inputProps={{ inputMode: 'numeric', style: { textAlign: 'right' } }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={5}
              sx={{
                px: 1.25,
                py: 0.75,
                borderLeft: { xs: 0, md: '1px solid rgba(0,0,0,0.08)' },
                color: 'text.disabled',
              }}
            >
              <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                — not applicable
              </Typography>
            </Grid>
          </Grid>
          {chbNT && (String(row.sInstructNhontrachLst ?? '') || instructedNT > toNum(row.sInstructNhontrachDo)) ? (
            <Box sx={{ px: 1.25, pb: 0.75, bgcolor: (t) => alpha(t.palette.warning.main, 0.08) }}>
              {String(row.sInstructNhontrachLst ?? '') ? (
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                  Booked DO: <b>{String(row.sInstructNhontrachLst)}</b>
                </Typography>
              ) : null}
              {instructedNT > toNum(row.sInstructNhontrachDo) ? (
                <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 600 }}>
                  ⚠ Planned but DO not fully booked — add DO after saving.
                </Typography>
              ) : null}
            </Box>
          ) : null}

          {/* Row: Direct P-S */}
          <Grid container alignItems="center">
            <Grid item xs={12} md={4} sx={{ px: 1.25, py: 0.75 }}>
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={chbDirect}
                    onChange={(e) => toggleChbDirect(e.target.checked)}
                  />
                }
                label={<Typography variant="body2">Direct P-S</Typography>}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
              sx={{
                px: 1.25,
                py: 0.75,
                borderLeft: { xs: 0, md: '1px solid rgba(0,0,0,0.08)' },
              }}
            >
              <TextField
                size="small"
                fullWidth
                type="text"
                value={formatThousand(toNum(insDirect))}
                onChange={(e) => setInsDirect(e.target.value.replace(/[^\d]/g, ''))}
                disabled={!chbDirect}
                inputProps={{ inputMode: 'numeric', style: { textAlign: 'right' } }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={5}
              sx={{
                px: 1.25,
                py: 0.75,
                borderLeft: { xs: 0, md: '1px solid rgba(0,0,0,0.08)' },
              }}
            >
              <Autocomplete
                size="small"
                fullWidth
                // Inject value đã set (autoload/Edit) vào options nếu thiếu → hết
                // warning "value invalid" + chip hiển thị đúng (memory inject-current-value).
                options={
                  insSide && !candidates.some((c) => c.contract_no === insSide)
                    ? [{ contract_no: insSide } as typeof candidates[number], ...candidates]
                    : candidates
                }
                value={candidates.find((c) => c.contract_no === insSide)
                  ?? (insSide ? ({ contract_no: insSide } as typeof candidates[number]) : null)}
                onChange={(_e, v) => setInsSide(v?.contract_no ?? '')}
                getOptionLabel={(o) => o ? formatPurchaseOption(o) : ''}
                isOptionEqualToValue={(a, b) => a.contract_no === b.contract_no}
                disabled={!chbDirect}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select Purchase"
                    InputLabelProps={{ shrink: true }} />
                )}
              />
            </Grid>
          </Grid>
          {chbDirect && (String(row.sDirectPLst ?? '') || instructedDirect > toNum(row.sDirectPDo)) ? (
            <Box sx={{ px: 1.25, pb: 0.75, bgcolor: (t) => alpha(t.palette.warning.main, 0.08) }}>
              {String(row.sDirectPLst ?? '') ? (
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                  Booked DO: <b>{String(row.sDirectPLst)}</b>
                </Typography>
              ) : null}
              {instructedDirect > toNum(row.sDirectPDo) ? (
                <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 600 }}>
                  ⚠ Planned but DO not fully booked — add DO after saving.
                </Typography>
              ) : null}
            </Box>
          ) : null}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* ── Match Date + Note ───────────────────────────────────────── */}
        <Grid container spacing={1.25}>
          <Grid item xs={12} sm={4}>
            <DatePicker
              label="Match Date"
              format="DD-MMM-YYYY"
              value={matchDate}
              onChange={(d) => setMatchDate(d)}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  InputLabelProps: { shrink: true },
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField
              size="small"
              fullWidth
              label="Note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        {/* Summary: sum input vs quantity_kg để user thấy mismatch ngay. */}
        <Box
          sx={{
            mt: 2,
            p: 1,
            borderRadius: 1,
            bgcolor: (t) => alpha(t.palette.text.primary, 0.03),
            border: (t) => `1px dashed ${alpha(t.palette.text.primary, 0.15)}`,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Sum input:{' '}
            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {formatThousand(
                (chbBL ? toNum(insBL) : 0) +
                  (chbNT ? toNum(insNT) : 0) +
                  (chbDirect ? toNum(insDirect) : 0),
              )}
            </Box>{' '}
            / ContractQuant: <strong>{formatThousand(quantityKg)}</strong>
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 1.5, sm: 2 }, py: { xs: 0.75, sm: 1.25 } }}>
        <Button onClick={onClose} disabled={saving} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={!canEdit || saving || !contractNo}
          variant="contained"
          sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: alpha(ACCENT, 0.85) } }}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>

      {/* Create DO cho match vừa tạo (Save → mở popup). Đóng popup = đóng cả dialog Edit. */}
      <CreateDoFromPlanDialog
        open={doOpen}
        scenario={doScenario}
        mode={doMode}
        seeds={doSeeds}
        editRows={doEditRows}
        onClose={() => {
          setDoOpen(false)
          onClose()
        }}
      />

      {/* Loại bị đưa về 0 mà contract còn DO → confirm xoá DO + update plan. */}
      <ConfirmDialog
        open={confirmDelSaveOpen}
        title="Update Sale Plan"
        message={
          orphanedDos.length > 0
            ? `Saving will delete these DO(s): ${orphanedDos
                .map((d) => d.doNo || `#${d.deliveryOrderScsId}`)
                .join(', ')}. Continue?`
            : ''
        }
        confirmLabel="Delete DO(s) + Update"
        danger
        loading={saving || deletingDos}
        onConfirm={() => {
          setConfirmDelSaveOpen(false)
          void performSaveAndDo(true)
        }}
        onClose={() => setConfirmDelSaveOpen(false)}
      />
    </>
  )
}
